import React, { useState, useEffect, useRef } from 'react'
import { StyledEditor } from './editor.style';
import ReactQuill, { Quill } from 'react-quill';
import Tabs from 'component/commonComponents/Tabs';
import Tab from 'component/commonComponents/Tabs/Tab';
import Panel from 'component/commonComponents/Tabs/Panel';
import parse from 'html-react-parser';
import 'react-quill/dist/quill.snow.css';
import HlxButton from 'component/commonComponents/button/button';
import QuillResizeImage from 'quill-resize-image';

Quill.register("modules/resize", QuillResizeImage);

export interface IEditorProps {
  content: string;
  onSubmit: (content: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  modules?: any;
  formats?: any;
  locale: {
    edit: string;
    preview: string;
    update: string;
  }
  onContentValidate: (errorMsg: string) => void;
};

export enum EditorTabs {
  EDITOR = "editor",
  PREVIEW = "preview"
};

// This constant using for validate the input on the editor.
export enum validateErrorMsg {
  OVERSIZE = "OVERSIZE",
  LARGE_IMAGE = "LARGE_IMAGE",
  EMPTY_IMAGE = "EMPTY_IMAGE",
}

/**
 * The number in this setting is refered to the size of Azure config. 
 * It just contains below 16Kb so that the editor should limit the content size and image upload. 
 * 
 */
const maxSizeSettings = {
  content: 10300,
  image: 9000,
}

/**
 * This function using to converting the File type to the Image type 
 * and return the Image Source for adding the Image Element to the Editor. 
 */

function fileToImageSrc(file: File) {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  return new Promise((resolve, reject) => {
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
  });
}

const Editor = (props: IEditorProps) => {
  const { content, onSubmit, placeholder, readOnly, modules, formats, locale, onContentValidate } = props;
  const [tabIndex, setTabIndex] = useState(EditorTabs.EDITOR);

  const [editorContent, setEditorContent] = useState(content);

  const [errorMsg, setErrorMsg] = useState('');
  const quillRef: any = useRef();

  const validateContentSize = (value: any): boolean => {
    const contentSize = new Blob([value]).size;
    if(contentSize >= maxSizeSettings.content){
      return true;
    }
    return false;
  }

  useEffect(() => {
    if(errorMsg){
      onContentValidate(errorMsg);
      setErrorMsg('');
    }
  }, [errorMsg]);

  useEffect(() => {
    setEditorContent(content);
    const qlEditor = document.querySelector('.ql-editor');
    if (qlEditor) {
      qlEditor.innerHTML = content;
    }
  }, [content]);


  /**
   * This useEffect function handle for uploading the image into the Editor. 
   */
  useEffect(() => {
    quillRef.current
      .getEditor()
      .getModule('toolbar')
      .addHandler('image', () => {
        const input = document.createElement('input')
        input.setAttribute('type', 'file')
        input.setAttribute('accept', 'image/*')
        input.click()
        input.onchange = async () => {
          if (!input.files || !input?.files?.length || !input?.files?.[0]){
            setErrorMsg(validateErrorMsg.EMPTY_IMAGE);
            return;
          }
          const editor = quillRef?.current?.getEditor();
          const file = input.files[0];
          if(file?.size >= maxSizeSettings.image) {
            setErrorMsg(validateErrorMsg.LARGE_IMAGE);
            return;
          }
          const img = await fileToImageSrc(file);
          const range = editor.getSelection(true)
          editor.insertEmbed(
            range.index,
            'image',
            img
          )
        }
      })
  }, [quillRef])

  const onChange = (value: string) => {
    setEditorContent(value);
  };

  const handleSubmitEditor = () => {
    const editor = quillRef?.current?.getEditor();
    const currentContentSize = editor?.root?.innerHTML;
    const isContentOversize = validateContentSize(currentContentSize);
    if(isContentOversize){
      setErrorMsg(validateErrorMsg.OVERSIZE);
      return;      
    }
    onSubmit(editorContent);
    setErrorMsg('');
  }

  return (
    <>
      <StyledEditor>
        <Tabs
          fullWidth
          activeTab={tabIndex}
          onChange={(tab: EditorTabs) => setTabIndex(tab)}
          onClick={(tab: EditorTabs) => setTabIndex(tab)}
        >
          <Tab label={EditorTabs.EDITOR} id="btn-editor-edit">
            {locale.edit}
          </Tab>
          <Tab label={EditorTabs.PREVIEW} id="btn-editor-preview">
            {locale.preview}
          </Tab>

          <Panel label={EditorTabs.EDITOR}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={editorContent}
              onChange={onChange}
              placeholder={placeholder}
              readOnly={readOnly}
              modules={modules}
              formats={formats}
            />
            <HlxButton
              label={locale.update}
              onClick={handleSubmitEditor}
              id="btn-editor-update"
            />
          </Panel>
          <Panel label={EditorTabs.PREVIEW}>
            {parse(editorContent)}
          </Panel>
        </Tabs>
      </StyledEditor>
    </>
  )
};

export const defaultProps: IEditorProps = {
  content: ``,
  onSubmit: (content: string) => console.log(content),
  placeholder: "Enter text here...",
  readOnly: false,
  modules: {
    toolbar: [
      // [{ size: [] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' },
      // [{ 'script': 'sub'}, { 'script': 'super' }],
      { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      // [{ 'align': [] }],
      ['clean']
    ],
    clipboard: {
      // toggle to add extra line breaks when pasting HTML:
      matchVisual: false,
    },
    resize: {
      locale: {},
    },
  },
  formats: [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'code-block', 'align',
  ],
  locale: {
    edit: "Edit",
    preview: "Preview",
    update: "Update"
  },
  onContentValidate: (errorMsg: string) => console.log(errorMsg),
};

Editor.defaultProps = defaultProps;

export default Editor;